/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerSchedule.ts
 * PURPOSE: TanStack Query hooks cho màn Bảng phân ca — Release v0 WP-6b / SC-009-SCHEDULE-GRID
 *          + 2026-09-14: useCopyRangePreview/useCopyRangeCommit — modal "Sao
 *          chép lịch" khoảng ngày tuỳ ý [17]/[17b] (generalize copy-from-week).
 *          + 2026-09-14 (tiếp): useScheduleAuditHistory — panel [6] "Nhật ký
 *          thay đổi lịch", cursor pagination qua useInfiniteQuery ("Xem thêm
 *          (cũ hơn)" — KHÔNG số trang).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Xếp ca tuần (Manager schedule grid)
 * - Impact: Revenue Critical + Compliance (trần giờ tuần, nghỉ giữa 2 ca, D-6 guard PUBLISHED)
 * - Stakeholders: Branch Managers, Shift Managers
 *
 * @technical-architecture
 * - Layer: Custom Hooks (Business Logic)
 * - Pattern: TanStack Query v5 (useQuery, useMutation, useQueryClient)
 * - Dependencies: @tanstack/react-query, managerApi
 *
 * @cross-references
 * - API Service: frontend/src/services/api/managerApi.ts (schedule section)
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py (WP-6a)
 * - Page: frontend/src/pages/manager/ManagerSchedulePage.tsx
 * - Modal: frontend/src/pages/manager/schedule/CopyScheduleModal.tsx
 * - Sibling pattern: frontend/src/hooks/useManagerAttendance.ts
 * ===============================================================================
 */

import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  managerApi,
  type BulkAssignHoursPayload,
  type BulkAssignPayload,
  type BulkOffPayload,
  type CopyRangeRequestPayload,
  type ImportAssignment,
  type QuickEntryPayload,
  type ScheduleCellPayload,
  type WeekApprovalPayload,
  type WeeklyGridParams,
} from "../services/api/managerApi";

export const MANAGER_SCHEDULE_QUERY_KEYS = {
  all: ["manager", "schedule"] as const,
  grid: (params: WeeklyGridParams) =>
    ["manager", "schedule", "grid", params.week_start, params.work_area_id ?? ""] as const,
  shiftCodes: ["manager", "schedule", "shift-codes"] as const,
  branches: ["manager", "schedule", "branches"] as const,
};

/** Lưới [5] phân ca tuần (+ lọc khu vực [2] server-side) */
export function useWeeklyGrid(params: WeeklyGridParams) {
  return useQuery({
    queryKey: MANAGER_SCHEDULE_QUERY_KEYS.grid(params),
    queryFn: () => managerApi.getWeeklyGrid(params),
    staleTime: 15_000,
    retry: 2,
  });
}

/**
 * Nhiều tuần cùng lúc (S-MGR-VIEWMODE 2026-08-09) — view chỉ-xem "Nhiều tuần"
 * trên màn Bảng phân ca. Gọi lại ĐÚNG API weekly-grid hiện có, mỗi tuần 1
 * query — KHÔNG có endpoint backend mới, share cache key với useWeeklyGrid
 * nên tuần nào đã tải (vd tuần hiện tại) là cache-hit tức thì.
 */
export function useMultiWeekGrid(weekStarts: string[], workAreaId?: string) {
  return useQueries({
    queries: weekStarts.map((weekStart) => ({
      queryKey: MANAGER_SCHEDULE_QUERY_KEYS.grid({
        week_start: weekStart,
        work_area_id: workAreaId,
      }),
      queryFn: () =>
        managerApi.getWeeklyGrid({
          week_start: weekStart,
          ...(workAreaId ? { work_area_id: workAreaId } : {}),
        }),
      staleTime: 15_000,
      retry: 2,
    })),
  });
}

/** Danh mục mã ca cho combobox [13] (ít đổi — cache lâu) */
export function useScheduleShiftCodes() {
  return useQuery({
    queryKey: MANAGER_SCHEDULE_QUERY_KEYS.shiftCodes,
    queryFn: () => managerApi.getScheduleShiftCodes(),
    staleTime: 5 * 60_000,
    retry: 2,
  });
}

/** 2026-08-12: danh mục cơ sở cho combobox "Điều động cơ sở khác" trong popover [13] */
export function useTransferBranches() {
  return useQuery({
    queryKey: ["manager", "schedule", "transfer-branches"],
    queryFn: () => managerApi.getTransferBranches(),
    staleTime: 10 * 60_000,
  });
}

/** Danh mục chi nhánh (dropdown điều động [+] Phân ca — VĐ1) — ít đổi, cache lâu */
export function useManagerBranches() {
  return useQuery({
    queryKey: MANAGER_SCHEDULE_QUERY_KEYS.branches,
    queryFn: () => managerApi.getManagerBranches(),
    staleTime: 5 * 60_000,
    retry: 2,
  });
}

/** Invalidate mọi query lưới phân ca sau mutation (đồng bộ ô + Tổng + banner [4] LIVE) */
function useInvalidateScheduleGrid() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MANAGER_SCHEDULE_QUERY_KEYS.all });
}

/**
 * Popup [+] Phân ca hàng loạt (VĐ1) — N nhân viên × khoảng ngày × 1 mã ca,
 * kèm điều động liên chi nhánh. Invalidate CẢ lưới phân ca (chi nhánh gốc)
 * LẪN "manager","staff" (chi nhánh đích cần thấy NV điều động ngay —
 * không đợi 30s staleTime của useManagerStaff).
 */
export function useBulkAssignSchedule() {
  const invalidateGrid = useInvalidateScheduleGrid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkAssignPayload) => managerApi.bulkAssignSchedule(payload),
    onSuccess: () => {
      invalidateGrid();
      queryClient.invalidateQueries({ queryKey: ["manager", "staff"] });
    },
  });
}

/**
 * [OT] "Phân theo giờ": khối giờ tuỳ chỉnh (KHÔNG gắn mã ca) + tuỳ chọn
 * "Tính là OT", section ĐỘC LẬP với "＋ Thêm ca" trong cùng popover. Cùng
 * invalidation với useBulkAssignSchedule (chi nhánh gốc + đích).
 */
export function useBulkAssignHoursSchedule() {
  const invalidateGrid = useInvalidateScheduleGrid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkAssignHoursPayload) => managerApi.bulkAssignHoursSchedule(payload),
    onSuccess: () => {
      invalidateGrid();
      queryClient.invalidateQueries({ queryKey: ["manager", "staff"] });
    },
  });
}

/**
 * [2f] BR-SCHD-013 (2026-09-07, 🆕 CHƯA CÓ BR chính thức — wireframe là
 * nguồn đầu tiên): "Nghỉ hàng loạt" — popover mở sau kéo-chọn hình chữ nhật
 * (ngày × nhân viên), bỏ hết ca ĐANG CÓ trong vùng chọn.
 */
export function useBulkOffSchedule() {
  const invalidateGrid = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (payload: BulkOffPayload) => managerApi.bulkOffSchedule(payload),
    onSuccess: invalidateGrid,
  });
}

/**
 * [5] BR-SCHD-011 (2026-09-07, 🆕 CHƯA CÓ BR chính thức — wireframe là nguồn
 * đầu tiên): "Nhập nhanh bằng văn bản" — FE đã parse text thành assignments,
 * hộp KHÔNG tự đóng sau khi gán (còn dán tiếp) nên KHÔNG invalidate ở đây;
 * caller tự invalidate + giữ hộp mở theo đúng state NHẬP-NHANH-GÁN-XONG.
 */
export function useQuickEntrySchedule() {
  const invalidateGrid = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (payload: QuickEntryPayload) => managerApi.quickEntrySchedule(payload),
    onSuccess: invalidateGrid,
  });
}

/** Popover [13]: gán ca / xoá 1 ca / Nghỉ (bỏ hết ca) — 422 khi mode Chặn vi phạm */
export function useUpdateScheduleCell() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (payload: ScheduleCellPayload) => managerApi.updateScheduleCell(payload),
    onSuccess: invalidate,
  });
}

/** Dialog [15]/[16] Xuất bản (+ [Xuất bản lại] D-6) — 422 khi mode Chặn còn vi phạm */
export function usePublishScheduleWeek() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (weekStart: string) => managerApi.publishScheduleWeek(weekStart),
    onSuccess: invalidate,
  });
}

/** Popup [18] [Hủy thay đổi] — khôi phục về bản xuất bản gần nhất (422 nếu tuần chưa từng xuất bản) */
export function useDiscardScheduleWeekChanges() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (weekStart: string) => managerApi.discardScheduleWeekChanges(weekStart),
    onSuccess: invalidate,
  });
}

/** Nút [11] Lưu nháp (audit checkpoint — KHÔNG gỡ khoá D-6) */
export function useSaveScheduleDraft() {
  return useMutation({
    mutationFn: (weekStart: string) => managerApi.saveScheduleDraft(weekStart),
  });
}

/** Modal [17]→[18] Xem trước nhập (KHÔNG ghi DB) */
export function useImportSchedulePreview() {
  return useMutation({
    mutationFn: ({ file, weekStart }: { file: File; weekStart: string }) =>
      managerApi.importSchedulePreview(file, weekStart),
  });
}

/** Modal [18] [Nhập N gán hợp lệ] → toast "Đã nhập N gán lịch" + reload lưới */
export function useImportScheduleCommit() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: ({
      weekStart,
      assignments,
    }: {
      weekStart: string;
      assignments: ImportAssignment[];
    }) => managerApi.importScheduleCommit(weekStart, assignments),
    onSuccess: invalidate,
  });
}

/** [Sao chép tuần trước] (S-MGR-COPY 2026-08-09) → reload lưới đích */
export function useCopyScheduleFromWeek() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: ({
      sourceWeekStart,
      targetWeekStart,
    }: {
      sourceWeekStart: string;
      targetWeekStart: string;
    }) => managerApi.copyScheduleFromWeek(sourceWeekStart, targetWeekStart),
    onSuccess: invalidate,
  });
}

// [Sao chép lịch] khoảng ngày tuỳ ý (2026-09-14, generalize copy-from-week ở
// trên — WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 [17]/[17b]).

/**
 * Modal "Sao chép lịch" — xem trước (dry_run, KHÔNG ghi DB). Dùng `useQuery`
 * (không phải `useMutation`) dù backend là POST: đây là dữ liệu DẪN XUẤT
 * THUẦN từ 4 tham số khoảng (source/target from/to) — tự fetch lại mỗi khi
 * modal đổi khoảng nguồn (đúng ngữ nghĩa "derived data" của useQuery), không
 * cần mở/đóng thủ công như 1 hành động rời rạc (khác `useImportSchedulePreview`
 * — trigger bởi 1 sự kiện CHỌN TỆP, không phải tham số đổi liên tục).
 * `enabled: params !== null` — cho phép caller tạm khoá fetch (vd khoảng
 * nguồn chưa hợp lệ); `placeholderData` giữ bảng preview CŨ trong lúc tải lại
 * (v5 thay `keepPreviousData`), tránh bảng nhấp nháy rỗng giữa 2 lần đổi khoảng.
 */
export function useCopyRangePreview(params: CopyRangeRequestPayload | null) {
  return useQuery({
    queryKey: ["manager", "schedule", "copy-range-preview", params],
    queryFn: () => managerApi.copyRangePreview(params as CopyRangeRequestPayload),
    enabled: params !== null,
    placeholderData: (prev) => prev,
  });
}

/** [Sao chép K ca] — server TÍNH LẠI TỪ ĐẦU (vá L-03 của prototype), reload lưới đích */
export function useCopyRangeCommit() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (payload: CopyRangeRequestPayload) => managerApi.copyRangeCommit(payload),
    onSuccess: invalidate,
  });
}

// ── Employee tự đăng ký ca → Manager duyệt (2026-08-09) ─────────────────────

/** Popover: [Duyệt] 1 ca Employee tự đề xuất → reload lưới */
export function useApproveScheduleProposal() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (scheduleId: string) => managerApi.approveScheduleProposal(scheduleId),
    onSuccess: invalidate,
  });
}

/** Popover: [Từ chối] 1 ca Employee tự đề xuất (lý do bắt buộc) → reload lưới */
export function useRejectScheduleProposal() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: ({ scheduleId, reason }: { scheduleId: string; reason: string }) =>
      managerApi.rejectScheduleProposal(scheduleId, reason),
    onSuccess: invalidate,
  });
}

/** [Duyệt tuần] hàng loạt mọi ca PENDING của 1 nhân viên trong 1 tuần → reload lưới */
export function useReviewWeekProposals() {
  const invalidate = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (payload: WeekApprovalPayload) => managerApi.reviewWeekProposals(payload),
    onSuccess: invalidate,
  });
}

// ── Đổi ca (Shift Swap) — Variant B, DD-0106 (2026-08-30) ───────────────────

export const SWAP_REVIEW_QUERY_KEY = ["manager", "schedule", "swap-pending-review"] as const;

/** Màn "Đổi ca": danh sách request đã được NV target đồng ý, chờ Quản Lý duyệt */
export function usePendingSwapReviews() {
  return useQuery({
    queryKey: SWAP_REVIEW_QUERY_KEY,
    queryFn: () => managerApi.getPendingSwapReviews(),
    staleTime: 15_000,
    retry: 2,
  });
}

/** Quản Lý bấm Duyệt/Từ chối 1 request đổi ca */
export function useApproveSwapRequest() {
  const queryClient = useQueryClient();
  const invalidateGrid = useInvalidateScheduleGrid();
  return useMutation({
    mutationFn: (payload: { swap_request_id: string; approved: boolean; manager_notes?: string }) =>
      managerApi.approveSwapRequest(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SWAP_REVIEW_QUERY_KEY });
      invalidateGrid();
    },
  });
}

// ── [6] Panel "Nhật ký thay đổi lịch" (2026-09-14) ──────────────────────────

/**
 * Panel [6] — thanh điều hướng tuần RIÊNG của panel, ĐỘC LẬP hoàn toàn với
 * tuần đang xem trên lưới chính (wireframe ANNOTATION [6]: "sau đó ĐỘC LẬP
 * hoàn toàn với lưới chính, không đồng bộ theo [CAL] toolbar"). Cursor
 * pagination qua `useInfiniteQuery` — nút "Xem thêm (cũ hơn)" gọi
 * `fetchNextPage()`, KHÔNG có khái niệm số trang (khớp `next_cursor`).
 * `enabled` do caller kiểm soát (chỉ fetch khi panel đang BẬT — tránh gọi
 * API khi panel tắt).
 */
export function useScheduleAuditHistory(params: {
  weekStart: string;
  limit?: number;
  enabled?: boolean;
}) {
  const { weekStart, limit = 25, enabled = true } = params;
  return useInfiniteQuery({
    queryKey: ["manager", "schedule", "audit-history", weekStart, limit],
    queryFn: ({ pageParam }) =>
      managerApi.getScheduleAuditHistory({
        week_start: weekStart,
        limit,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: enabled && !!weekStart,
    staleTime: 15_000,
    // KHÔNG override `retry` ở đây — để QueryClient (root `retry: 1` ở
    // main.tsx, hoặc `retry: false` trong QueryClient riêng của test) tự
    // quyết. Đã THỬ hardcode `retry: 1` tại query-level lúc đầu — đúng ý đồ
    // "panel có nút [Thử lại] tường minh, không cần chờ nhiều lượt retry
    // ngầm" NHƯNG một retry TẠI QUERY LEVEL luôn THẮNG `retry: false` của
    // QueryClient test (TanStack Query v5: option query-level > defaultOptions
    // QueryClient) — làm test lỗi/mất-mạng phải đợi hết vòng backoff mới tới
    // state lỗi, timeout giả (đo thật: ScheduleAuditPanel.test.tsx 2 test lỗi
    // treo skeleton >1s trước khi sửa). Root QueryClient main.tsx đã có sẵn
    // `retry: 1` — bỏ override ở đây giữ NGUYÊN hành vi production, chỉ gỡ
    // xung đột với test.
  });
}
